import { useState } from 'react';
import type { ReactElement } from 'react';
import { paisa } from '@fleet/shared';
import { useDriversQuery } from '@/api/endpoints';
import type { Driver } from '@/api/types';
import { useCan } from '@/app/session';
import { LinkButton } from '@/design/Button';
import { Identifier, MoneyText } from '@/design/Data';
import { DataTable } from '@/design/DataTable';
import type { Column } from '@/design/DataTable';
import { Checkbox } from '@/design/Field';
import { Icon } from '@/design/icons';
import { Panel } from '@/design/Panel';
import { StatusPill } from '@/design/StatusPill';
import { DRIVER_AVAILABILITY } from './labels';

/**
 * Drivers.
 *
 * A licence that has expired is the counter's problem before it is the driver's,
 * so the expiry date is a column rather than something buried in a form — and
 * an expired one says so in words, not only in red.
 */

function licenceState(expiry: string | null | undefined): {
  readonly label: string;
  readonly expired: boolean;
} {
  if (expiry === null || expiry === undefined || expiry === '') {
    return { label: 'Not recorded', expired: false };
  }
  const expiryDate = new Date(`${expiry}T00:00:00Z`);
  const today = new Date();
  return {
    label: expiryDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    expired: expiryDate.getTime() < today.getTime(),
  };
}

export function DriversPage(): ReactElement {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, isError, refetch } = useDriversQuery({ includeInactive });
  const canManage = useCan('settings.manage');

  const columns: readonly Column<Driver>[] = [
    { key: 'name', header: 'Name', render: (driver) => driver.name },
    {
      key: 'phone',
      header: 'Phone',
      render: (driver) => (
        <a href={`tel:${driver.phone}`} className="identifier underline-offset-2 hover:underline">
          {driver.phone}
        </a>
      ),
    },
    {
      // Not `secondary`, and not `?? '—'`.
      //
      // It was both, which meant it was hidden on a phone and — because the
      // API sends an unrecorded licence as an empty string rather than null —
      // `??` never fired, so on a wide screen the column was simply blank. A
      // blank cell reads as a broken screen; "Not recorded" reads as a job.
      key: 'licence',
      header: 'Licence',
      render: (driver) =>
        driver.licence_no === undefined || driver.licence_no === '' ? (
          <span className="text-attention">Not recorded</span>
        ) : (
          <Identifier>{driver.licence_no}</Identifier>
        ),
    },
    {
      key: 'expiry',
      header: 'Licence expires',
      render: (driver) => {
        const licence = licenceState(driver.licence_expiry);
        return licence.expired ? (
          <span className="font-medium text-alert">{licence.label} — expired</span>
        ) : (
          <span className="tnum">{licence.label}</span>
        );
      },
    },
    {
      key: 'allowance',
      header: 'Daily allowance',
      numeric: true,
      secondary: true,
      render: (driver) => <MoneyText amount={paisa(driver.daily_allowance ?? 0)} />,
    },
    {
      // "Available or not", as the client asked for, rather than "working or
      // not". Somebody scanning this column is looking for a driver for this
      // afternoon; whether the person is still employed is the answer to a
      // different question, and it is still here as the third state.
      key: 'active',
      header: 'Status',
      render: (driver) => (
        <StatusPill
          status={
            DRIVER_AVAILABILITY[driver.availability ?? 'available'] ??
            DRIVER_AVAILABILITY['available']!
          }
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-display font-semibold">Drivers</h1>
        {canManage && (
          <LinkButton to="/drivers/new" intent="primary" size="touch">
            <Icon name="plus" width="16" height="16" />
            Add driver
          </LinkButton>
        )}
      </div>

      <Panel
        title="Drivers"
        note="Assigned to a booking at confirmation, and changeable up to handover"
        current
        actions={
          <Checkbox
            label="Include people who have left"
            checked={includeInactive}
            onChange={setIncludeInactive}
          />
        }
      >
        <DataTable
          columns={columns}
          rows={data?.results ?? []}
          rowKey={(driver) => driver.id}
          rowHref={canManage ? (driver) => `/drivers/${driver.id}` : undefined}
          isLoading={isLoading}
          isError={isError}
          onRetry={() => void refetch()}
          errorHeadline="The drivers did not load"
          errorDetail="The server did not answer. Check the connection and load them again."
          empty={{
            headline: 'No drivers yet',
            detail:
              'Add a driver with their licence number and expiry date. The expiry is what lets the system warn you before a renewal is overdue.',
            action: canManage ? (
              <LinkButton to="/drivers/new" intent="primary">
                <Icon name="plus" width="16" height="16" />
                Add driver
              </LinkButton>
            ) : undefined,
          }}
        />
      </Panel>
    </div>
  );
}
